<template>
  <div class="ppp" data-test="patient-profile-panel">
    <PatientProfileView
      mode="page"
      :payload="payload"
      :data-loading="dataLoading"
      :data-error="dataError"
      :insurance="insurance"
      :insurance-loading="insuranceLoading"
      :signed-plans="signedPlans"
      :signed-plans-loading="signedPlansLoading"
      :tasks="patientTasks"
      :tasks-state="tasksState"
      :employees="employees || []"
      :followups="followups"
      :tiles="tiles"
      :alert-details="alertDetails"
      :attention-note="attentionNote"
      :today="today"
      :date-format="dateFormat"
      :timezone="timezone"
      :capabilities="{ txsign: canAccessTxPresenter, insverify: canAccessInsuranceVerification }"
      :tab="tab"
      :extra-tabs="extraTabs"
      :persistent-tabs="['financials']"
      @tab="setTab"
      @retry="load"
    >
      <template #avatar>
        <div class="ppp__avatar">
          <PatientPhotoAvatar :site-id="siteID" :pat-num="patientNum" :enabled="!!actionGates.patientProfilePhotoEnabled" />
        </div>
      </template>

      <template #header-extra>
        <span v-if="patient && patient.Language === 'spa'" class="ppp__badge" title="Patient prefers Spanish">ES</span>
        <InsuranceStatusChip
          v-if="insurance"
          :status="insurance.status"
          :last-verified="insurance.lastVerified"
          :last-checked="insurance.lastChecked"
          :is-stale="insurance.isStale"
          :never-verified="insurance.neverVerified"
          :timezone="timezone"
          @navigate="onInsuranceChip"
        />
      </template>

      <!-- Inside the "Eligibility last verified" chip: Verify / Re-check and View benefits for the PRIMARY
           plan. Only a remote — the Insurance card below runs the check (one lock, one dispatch) and
           opens the benefits drawer. -->
      <template #verified-action>
        <InsuranceQuickVerify
          v-if="insuranceCardMounted && ivQuick && (ivQuick.canVerify || ivQuick.canViewBenefits)"
          :state="ivQuick"
          :patient-label="patientName || 'this patient'"
          @run="onQuickVerify"
          @benefits="onQuickBenefits"
        />
      </template>

      <template #actions>
        <PatientPanelActions v-if="patient" :actions="actionList" surface="Patient Profile" @action="onAction" />
      </template>

      <!-- Overview's Follow-ups card: the SAME queue rows as the tab below (drawer on a row click, date
           popover on the Follow-up cell), the first five. Only mounted when there are rows. -->
      <template #overview-followups="{ limit }">
        <FollowupsProfileSection
          embedded
          :max-rows="limit"
          :rows="followups"
          :profile-patient-id="patientID"
          :patient-name="patientName"
          :profile-facts="profileFacts"
        />
      </template>

      <template #tab-followups>
        <div class="pp-stack" data-test="ppp-followups">
          <div class="ppp__row">
            <button
              type="button"
              class="ppp__btn"
              :disabled="!startOptions.any"
              data-test="ppp-start-followup"
              @click="onAction('followup')"
            ><i class="fa fa-bell-o" aria-hidden="true"></i> Start a follow-up</button>
          </div>
          <FollowupsProfileSection
            v-if="followups && followups.length"
            :rows="followups"
            :profile-patient-id="patientID"
            :patient-name="patientName"
            :profile-facts="profileFacts"
          />
          <PanelCard v-else title="Follow-ups" icon="fa-bell-o" count="—">
            <p class="pp-empty">No open follow-ups for this patient.</p>
          </PanelCard>
        </div>
      </template>

      <template #tab-family>
        <PanelCard title="Family" icon="fa-users" :count="family.length || '—'" :table="family.length > 0" test-id="ppp-family">
          <table v-if="family.length" class="pp-tbl">
            <thead><tr><th>Name</th><th>Age</th><th></th></tr></thead>
            <tbody>
              <tr v-for="m in family" :key="m.PatNum">
                <td>{{ m.LName }}, {{ m.FName }}<span v-if="Number(m.PatNum) === Number(m.Guarantor)" class="pp-pill pp-pill--slate ppp__guar">Guarantor</span></td>
                <td class="pp-tbl__num">{{ m.Age }}</td>
                <td><router-link class="pp-link" :to="`/app/patientprofile/${siteID}-${m.PatNum}`">Open profile</router-link></td>
              </tr>
            </tbody>
          </table>
          <p v-else class="pp-empty">No other family members on this account.</p>
        </PanelCard>
      </template>

      <!-- The Financials tab is PERSISTENT (:persistent-tabs): this slot stays mounted and is only hidden
           on other tabs, so the Insurance card — which holds the double-billing lock — outlives a tab
           switch, and the header's Verify button always has a card to talk to. The view guards the slot
           with `payload && patient`; the v-if is belt and braces. -->
      <template #tab-financials>
        <FinancialsTab v-if="payload" :payload="payload" :alert-details="alertDetails" :date-format="dateFormat">
          <template #extra>
            <div v-if="canUseInsurancePanel" ref="insurancePanel">
              <InsurancePanel
                ref="insurancePanelCard"
                :site-id="siteID"
                :pat-num="patientNum"
                :patient-label="patientName || 'this patient'"
                :patient="insurancePatient"
                :timezone="timezone"
                :status="insurance"
                :loading="insuranceLoading"
                drawer-on-body
                @verified="loadInsurance"
                @primary-state="ivQuick = $event"
              />
            </div>
            <PanelCard v-else-if="allSettings && !insuranceVerificationEnabled" title="Insurance" icon="fa-shield" test-id="ppp-insurance-upsell">
              <FeaturePreview
                title="Enable Insurance Verification"
                description="Check eligibility and remaining benefits with the carrier, right from the patient profile"
                icon="fa fa-shield"
                :columns="['Plan', 'Remaining Benefits', 'Last Verified']"
                :sample-data="[['Primary Dental PPO', '$1,250', 'Today'], ['Secondary Dental Plan', '$800', 'Today']]"
                row-icon="fa fa-shield"
                feature-key="INSURANCE_VERIFICATION"
                feature-label="Insurance Verification"
                source-page="Patient Profile - Insurance"
              />
            </PanelCard>
          </template>
        </FinancialsTab>
      </template>

      <!-- Treatment tab, right-hand card while Tx Presenter is off: the classic page's blurred preview
           (settings loaded first, so it never flashes at an office that has the feature). -->
      <template v-if="allSettings && !canAccessTxPresenter" #treatment-side>
        <PanelCard title="Treatment plans signed" icon="fa-check-square-o" test-id="ppp-tx-upsell">
          <FeaturePreview
            title="Enable Treatment Presenter"
            description="Send treatment plans to patients and collect their signatures online"
            icon="fa fa-pencil-square-o"
            :columns="['Treatment Plan', 'Signed On', 'Consent Forms']"
            :sample-data="[
              ['Crown and Root Canal', '2 days ago', '2 signed'],
              ['Implant Placement', '1 week ago', '1 signed'],
              ['Periodontal Therapy', '3 weeks ago', '1 signed']
            ]"
            row-icon="fa fa-pencil-square-o"
            feature-key="TX_PRESENTER"
            feature-label="Treatment Presenter"
            source-page="Patient Profile - Treatment Plans Signed"
          />
        </PanelCard>
      </template>
    </PatientProfileView>

    <!-- The way back to the classic page. The component decides by role: always shown to a Super Admin,
         shown to an office user only while the area is at "invite" / "default" (it renders nothing
         otherwise, so the padding class goes on ITS root and no empty box is left behind). -->
    <FollowupsUiToggle class="ppp__toggle" target="old" page-key="patientProfileLayout" note-text="Super Admin preview — offices still see the classic Patient Profile." @toggle="$emit('switch-layout', 'old')" />

    <PatientPanelActionHost
      v-if="patient"
      ref="actionHost"
      :patient-data="patientData"
      :patient-num="patientNum"
      :site-id="siteID"
      :gates="actionGates"
      :signed-plans="signedPlans || []"
      :followup-context="followupContext"
      @busy="onBusy"
      @followup-saved="load"
      @open-tab="setTab"
    />

    <PatientSmsWidget v-if="patientData && patientData.PatNum" :patient="patientData" />

    <!-- The ONE Firestore listener on this patient's tasks (renders nothing). It lives on the page, not
         in a tab, so a tab switch can never tear it down; the Tasks tab draws its rows. -->
    <PatientTasksListener v-if="siteID" :site-id="siteID" :patient-num="patientNumInt" @state="tasksState = $event" />

    <!-- This page's ONE slide-out outlet (Send Form/Text/Email, Create Task, the follow-up drawer). -->
    <slideout-panel></slideout-panel>
  </div>
</template>

<script>
/**
 * The new Patient Profile page (design 1a, 2026-10-08) — Super Admins by default, office users per
 * STAGE.patientProfileLayout and their one office choice (src/utils/followups/uiVersion.js);
 * PatientProfileRoute.js picks this or the classic PatientProfile.vue, which is NOT edited.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * Draws the SAME PatientProfileView as the Schedule View patient panel, fed differently:
 *   data            the classic page's own PATIENT_PROFILE query (teamio/runReportQuery — side-effect
 *                   free, no NEW runquery), shaped by profileAdapter.js into the panel's payload
 *   Needs attention profileAlerts.js — computed here; schedule-view-service has no prod stack
 *   insurance       insuranceVerification/fetchPatientStatus (the classic chip's action)
 *   signed plans    txPresenter/fetchSignedPlansSummary (the classic section's action)
 *   tasks           PatientTasksListener.vue — the classic card's Firestore listener, mounted by this
 *                   page (so a tab switch never tears it down) — read here for the count and the
 *                   `task` alert, and drawn by the shared Tasks tab with the Tasks page's own rows
 *                   (TaskRow; a click opens its TaskDrawer through this page's outlet)
 *   actions         PatientPanelActions + PatientPanelActionHost (Patient Profile's own modals and
 *                   gates); Follow-up opens FollowupPanel (followupStart.js) through the host
 *
 * Page-only: the photo avatar, the insurance chip, the editable Follow-ups section, Family, the
 * InsurancePanel (Verify), the SMS widget. Not ported yet: the Super Admin AI Call modal.
 *
 * Verify from the header (2026-10-11): the "Eligibility last verified" chip carries a Verify / Re-check
 * button (InsuranceQuickVerify.vue) for the PRIMARY plan. It is a remote for the InsurancePanel — the
 * card reports `primary-state`, the button's confirm emits `run`, and onQuickVerify() calls the card's
 * runPrimaryFromHeader(). So the card stays the only thing that spends, with its one lock; to keep it
 * (and the lock) alive on every tab, the Financials tab is a persistent tab of the view. The chip's
 * "View benefits" (free read, not behind the spend gate) is the same kind of remote: `benefits` calls
 * the card's openPrimaryBenefits().
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import moment from 'moment-timezone';
import mixin from '@/mixin.js';
import PatientProfileView from '@/components/PatientPanel/PatientProfileView.vue';
import PatientPanelActions from '@/components/PatientPanel/PatientPanelActions.vue';
import PatientPanelActionHost from '@/components/PatientPanel/PatientPanelActionHost.vue';
import PanelCard from '@/components/PatientPanel/PanelCard.vue';
import FinancialsTab from '@/components/PatientPanel/tabs/FinancialsTab.vue';
import FeaturePreview from '@/components/FeaturePreview';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import InsuranceStatusChip from '@/pages/InsuranceVerification/components/InsuranceStatusChip.vue';
import InsurancePanel from './components/InsurancePanel.vue';
import InsuranceQuickVerify from './components/InsuranceQuickVerify.vue';
import PatientTasksListener from './components/PatientTasksListener.vue';
import PatientPhotoAvatar from './components/PatientPhotoAvatar.vue';
import PatientSmsWidget from './components/PatientSmsWidget';
import { adaptPatientProfileResults } from '@/utils/patientPanel/profileAdapter';
import { buildProfileAlertPayload, PROFILE_ALERT_KEYS } from '@/utils/patientPanel/profileAlerts';
import { followupStartOptions } from '@/utils/patientPanel/followupStart';
import { profilePatientData } from '@/utils/patientPanel/format';
import { resolveActionGates, buildActions, keySettings } from '@/utils/patientPanel/actionGates';
import { visibleAlertKeys, alertsForPatient, describeAlert } from '@/utils/scheduleView/alerts';
import { ALERT_META } from '@/utils/scheduleView/settingsForm';
import { dateFormatFor } from '@/utils/taskDraft';
import { profileFactsFrom } from '@/utils/followups/rowModel.js';
import { maskState } from '@/utils/phiMaskState';
import { extractApiError } from '@/utils/apiError';
import { parseSendNewLinkQuery } from '@/utils/actionCenterPaymentLink';
import { scheduleSendCatalogPrefetch } from '@/utils/sendPanelCatalog.js';

// Loaded on first render, as on the classic page: its follow-up actions import ToastComponent →
// @/store → the router, which would otherwise ride along with every import of this page.
const FollowupsProfileSection = () => import('@/components/Followups/FollowupsProfileSection.vue');

const META_BY_KEY = {};
ALERT_META.forEach((m) => { META_BY_KEY[m.key] = m; });

const TAB_IDS = ['overview', 'followups', 'treatment', 'appointments', 'communication', 'financials', 'forms', 'tasks', 'family'];

export default {
  name: 'PatientProfilePage',

  mixins: [mixin],

  components: {
    PatientProfileView, PatientPanelActions, PatientPanelActionHost, PanelCard, FinancialsTab, FeaturePreview,
    FollowupsUiToggle, InsuranceStatusChip, InsurancePanel, InsuranceQuickVerify, PatientTasksListener,
    PatientPhotoAvatar, PatientSmsWidget, FollowupsProfileSection,
  },

  props: {
    /** '<SiteUUID>-<PatNum>' — the route param, as on the classic page. */
    patientID: { type: String, required: true },
  },

  data() {
    return {
      payload: null,
      extras: { family: [], followups: [], belongsToLists: [] },
      followupsLoaded: false,
      dataLoading: false,
      dataError: '',
      insurance: null,
      insuranceLoaded: false,
      insuranceLoading: false,
      /** The Insurance card's `primary-state` (the header button's inputs), or null while no card is mounted. */
      ivQuick: null,
      signedPlansLoading: false,
      busy: { txplans: false, pay: false },
      /** The tasks listener's state ('loading' | 'ready' | 'failed'), reported by PatientTasksListener. */
      tasksState: 'loading',
      loadSeq: 0,
      /**
       * The open tab. Read from ?tab= ONCE (a deep link), then kept here — never written back to the
       * URL: Layout.vue keys <router-view> on $route.fullPath, so changing the query would remount
       * this page and re-run the whole PATIENT_PROFILE query on every tab click.
       */
      tab: TAB_IDS.indexOf(this.$route && this.$route.query && this.$route.query.tab) !== -1 ? this.$route.query.tab : 'overview',
      collectPaymentDeepLinkHandled: false,
    };
  },

  computed: {
    /** The PatNum, as the classic page reads it: the text after the last '-'. A string. */
    patientNum() {
      return this.patientID.substring(this.patientID.lastIndexOf('-') + 1);
    },
    /** Numeric — Firestore array-contains on taggedPatients is type-strict. */
    patientNumInt() {
      return parseInt(this.patientNum, 10);
    },
    timezone() {
      return (this.allSettings && this.allSettings.GENERAL_TIMEZONE && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },
    today() {
      return this.timezone ? moment().tz(this.timezone).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    },
    dateFormat() {
      return dateFormatFor(this.timezone);
    },
    extraTabs() {
      return [{ id: 'family', label: 'Family' }];
    },

    patient() {
      return (this.payload && this.payload.patient) || null;
    },
    patientData() {
      return profilePatientData(this.patient);
    },
    patientName() {
      return (this.patientData && this.patientData.PatientName) || '';
    },
    followups() {
      return this.followupsLoaded ? this.extras.followups : null;
    },
    family() {
      return this.extras.family || [];
    },
    belongsToLists() {
      return this.extras.belongsToLists || [];
    },
    signedPlans() {
      const s = this.$store.state.txPresenter && this.$store.state.txPresenter.signedPlansSummary;
      return (this.canAccessTxPresenter && s && Array.isArray(s.plans)) ? s.plans : [];
    },
    /** The listener's shared slot, filtered to THIS patient (the slot is shared app-wide). */
    patientTasks() {
      const all = (this.$store.state.tasks && this.$store.state.tasks.tasksTaggedWithPatient) || [];
      const n = this.patientNumInt;
      return all.filter(t => Array.isArray(t.taggedPatients) && t.taggedPatients.includes(n));
    },

    alertPayload() {
      return buildProfileAlertPayload({
        patient: this.patient,
        followups: this.followupsLoaded ? this.extras.followups : undefined,
        insurance: this.insuranceLoaded ? this.insurance : undefined,
        tasks: this.patientTasks,
        today: this.today,
      });
    },
    alertDetails() {
      const all = alertsForPatient(this.alertPayload, this.patientNum);
      const out = {};
      this.visibleKeys.forEach((k) => { out[k] = all[k]; });
      return out;
    },
    visibleKeys() {
      return this.patient ? visibleAlertKeys(this.alertPayload, this.patientNum, PROFILE_ALERT_KEYS) : [];
    },
    tiles() {
      return this.visibleKeys.map(key => ({
        key,
        icon: (META_BY_KEY[key] || {}).icon || 'fa-exclamation-circle',
        label: (META_BY_KEY[key] || {}).label || key,
        sentence: describeAlert(key, this.alertDetails[key]),
      }));
    },
    attentionNote() {
      return 'Recall, claims, forms and medical-history checks show on Schedule View.';
    },

    actionGates() {
      const customer = this.$store.state.customer || {};
      const getSite = this.$store.getters['customer/getSite'];
      const mask = maskState();
      return resolveActionGates({
        allSettings: keySettings(customer.settings),
        patient: this.patientData,
        site: typeof getSite === 'function' ? getSite(this.siteID) || null : null,
        isSuperAdmin: this.isSuperAdmin,
        canViewReviews: this.canViewReviews,
        maskOn: mask.userMask === true || mask.siteMask === true,
      });
    },
    canViewReviews() {
      return typeof this.$can === 'function' && this.$can('view', 'reviews');
    },
    startOptions() {
      return followupStartOptions({
        followups: this.extras.followups,
        unscheduledTx: (this.payload && this.payload.unscheduledTx) || [],
        patient: this.patient,
      });
    },
    actionList() {
      return buildActions(this.actionGates, {
        canViewReviews: this.canViewReviews,
        txLoading: this.busy.txplans,
        paymentOpening: this.busy.pay,
        followupReady: this.followupsLoaded,
      });
    },

    /** The classic page's InsurancePanel gate: the site flag AND the CASL subject (it can spend). */
    canUseInsurancePanel() {
      if (!this.insuranceVerificationEnabled) return false;
      return !!(typeof this.$can === 'function' && this.$can('view', 'insurance-verification'));
    },
    /**
     * Is the Insurance card mounted? The Financials slot draws it only for the spend gate above and
     * only once the patient is loaded (the view's `payload && patient` guard). The header button
     * exists only while it is — a state reported by a card that is gone must never keep it alive.
     */
    insuranceCardMounted() {
      return this.canUseInsurancePanel && !!this.payload && !!this.patient;
    },
    /** Classic insurancePatient: "Last, First" split on the FIRST comma. */
    insurancePatient() {
      const p = this.patient || {};
      return { patNum: this.patientNum, lName: (p.LName || '').trim(), fName: (p.FName || '').trim(), dob: p.Birthdate || '' };
    },
    profileFacts() {
      return profileFactsFrom({
        patient: this.patientData,
        unscheduledTreatments: (this.payload && this.payload.unscheduledTx) || [],
        brokenAppointments: (this.payload && this.payload.broken) || [],
      });
    },
    followupNotePlaceholder() {
      const s = this.allSettings && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG;
      return s && s.PropertyValue === 'TRUE' ? 'Optional. Add notes here. It will save to commlog...' : 'Optional. Add notes here...';
    },
    /** What the action host's "Start a follow-up" panel needs from this page. null until the rows are loaded. */
    followupContext() {
      if (!this.patient || !this.followupsLoaded) return null;
      return {
        patient: this.patient,
        followups: this.followups || [],
        unscheduledTx: (this.payload && this.payload.unscheduledTx) || [],
        lists: this.belongsToLists,
        today: this.today,
        notePlaceholder: this.followupNotePlaceholder,
      };
    },
  },

  watch: {
    patientID() {
      this.load();
    },
    // Settings arrive after login on a cold load: the gated fetches wait for them.
    allSettings(now, before) {
      if (now && !before && this.payload) this.loadSideData();
    },
    // The card is gone (flag or permission lost): drop what it last reported.
    insuranceCardMounted(now) {
      if (!now) this.ivQuick = null;
    },
  },

  created() {
    this.load();
    scheduleSendCatalogPrefetch(this.$store, this.siteID); // silent, delayed: the Send Form/Text/Email lists, so the first click is instant
  },

  methods: {
    async load() {
      if (!this.siteID || !/^\d+$/.test(this.patientNum)) {
        this.dataError = 'This patient link is not valid.';
        return;
      }
      const seq = ++this.loadSeq;
      this.dataLoading = true;
      this.dataError = '';
      try {
        const sets = await this.$store.dispatch('teamio/runReportQuery', {
          type: 'PATIENT_PROFILE',
          siteID: this.siteID,
          patNum: this.patientNum,
          _date: this.today,
          country: localStorage.getItem('country') || 'USA',
        });
        if (seq !== this.loadSeq) return;
        const { data, extras } = adaptPatientProfileResults(sets, this.patientNum);
        this.payload = data;
        this.extras = extras;
        this.followupsLoaded = true;
        this.loadSideData();
        this.$nextTick(this.maybeOpenCollectPaymentFromQuery);
      } catch (err) {
        if (seq !== this.loadSeq) return;
        console.error('[PatientProfilePage] load error:', err);
        this.dataError = extractApiError(err, 'Could not load this patient. Please try again.');
      } finally {
        if (seq === this.loadSeq) this.dataLoading = false;
      }
    },

    loadSideData() {
      this.loadInsurance();
      if (this.canAccessTxPresenter) {
        this.signedPlansLoading = true;
        this.$store.dispatch('txPresenter/fetchSignedPlansSummary', { siteID: this.siteID, patNum: this.patientNum })
          .catch(err => console.error('[PatientProfilePage] fetchSignedPlansSummary error:', err))
          .finally(() => { this.signedPlansLoading = false; });
      }
    },

    /** The classic loadInsuranceStatus: soft-gated, never paints one patient's plans under another. */
    async loadInsurance() {
      if (!this.canAccessInsuranceVerification || !this.siteID) return;
      const patNum = this.patientNum;
      this.insuranceLoading = true;
      try {
        const data = await this.$store.dispatch('insuranceVerification/fetchPatientStatus', { siteID: this.siteID, patNum });
        if (patNum !== this.patientNum) return;
        this.insurance = data && data.enabled ? data : null;
      } catch (err) {
        console.error('[PatientProfilePage] loadInsurance error:', err);
        if (patNum === this.patientNum) this.insurance = null;
      } finally {
        if (patNum === this.patientNum) {
          this.insuranceLoading = false;
          this.insuranceLoaded = true;
        }
      }
    },

    setTab(id) {
      if (TAB_IDS.indexOf(id) !== -1) this.tab = id;
    },

    /**
     * Action Center "Payment failed → Send new link" lands here with ?collectPayment=1 — the classic
     * page's maybeOpenCollectPaymentFromQuery, through the action host. Once per visit.
     */
    maybeOpenCollectPaymentFromQuery() {
      if (this.collectPaymentDeepLinkHandled) return;
      const prefill = parseSendNewLinkQuery(this.$route && this.$route.query);
      if (!prefill || !this.patientData || !this.siteID || !this.patientNum || !this.$refs.actionHost) return;
      this.collectPaymentDeepLinkHandled = true;
      this.$refs.actionHost.run('pay', { prefill });
    },

    onAction(key) {
      if (this.$refs.actionHost) this.$refs.actionHost.run(key);
    },

    onBusy({ key, busy }) {
      this.busy = { ...this.busy, [key]: busy };
    },

    /**
     * The header's "Run check": the user confirmed in the chip's own box, so hand it to the Insurance card —
     * the ONE place that runs a check (it holds the lock and always sends an explicit ordinal). Nothing is
     * dispatched from this page.
     */
    onQuickVerify() {
      const card = this.$refs.insurancePanelCard;
      if (card && typeof card.runPrimaryFromHeader === 'function') card.runPrimaryFromHeader();
    },

    /** The header's "View benefits": a free read of the PRIMARY's stored result, opened by the card's own drawer. */
    onQuickBenefits() {
      const card = this.$refs.insurancePanelCard;
      if (card && typeof card.openPrimaryBenefits === 'function') card.openPrimaryBenefits();
    },

    /** The chip opens the Insurance panel (Financials tab) when it is here, as the classic chip scrolls to it. */
    onInsuranceChip() {
      if (this.canUseInsurancePanel) {
        this.setTab('financials');
        this.$nextTick(() => {
          const el = this.$refs.insurancePanel;
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }
      if (this.$route.path !== '/app/insurance-verification') this.$router.push('/app/insurance-verification');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../components/PatientPanel/tokens';

.ppp {
  min-height: 100%;
  background: $pp-bg;
  font-family: $pp-font;
}

.ppp__avatar {
  flex: 0 0 auto;
  width: 60px;
  height: 60px;

  ::v-deep img,
  ::v-deep .patient-photo-avatar { width: 60px; height: 60px; }
}

.ppp__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 99px;
  background: $pp-blue-bg;
  color: $pp-blue-ink;
  font-size: 11px;
  font-weight: 700;
}

.ppp__guar { margin-left: 8px; }

.ppp__row {
  display: flex;
  justify-content: flex-end;
}

.ppp__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid $pp-brand-deep;
  border-radius: $pp-radius;
  background: $pp-brand-deep;
  box-shadow: $pp-shadow-primary;
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-brand-deeper; box-shadow: $pp-shadow-primary-hov; }
  &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
}

.ppp__toggle {
  padding: 0 26px 24px;
}

@media (max-width: $pp-phone-max) {
  .ppp__avatar { width: 48px; height: 48px; }
  .ppp__toggle { padding: 0 16px 120px; }
}
</style>
